const PRIVACY_SECTIONS = [
  {
    heading: "Who We Are",
    body: `This Privacy Notice for PDFTopicScout by Creatovate ("we," "us," or "our") describes how and why we might access, collect, store, use, and share your personal information when you use our services, including when you visit https://pdftopicscout.creatovate.biz or make purchases. Questions? Contact us at info@creatovate.biz.`,
  },
  {
    heading: "What Information We Collect",
    body: `We collect your email address when you subscribe or log in. We collect payment information through Stripe — we never see or store your card details directly. We store your saved topics (favourites) and search usage counts to provide the service. We do not collect your name, phone number, or any other personal identifiers beyond your email.`,
  },
  {
    heading: "How We Use Your Information",
    body: `Your email is used to send magic login links and essential service communications (billing receipts, service updates). We do not sell your data to third parties. We do not send marketing emails without your explicit consent. AI-generated content you produce using our tool belongs to you.`,
  },
  {
    heading: "Third-Party Services",
    body: `We use the following services to operate: Stripe (payments — stripe.com/privacy), Resend (transactional email), Upstash (data storage), Anthropic (AI features), and Vercel (hosting). Each operates under its own privacy policy. We only share the minimum data required for each service to function.`,
  },
  {
    heading: "Data Retention & Deletion",
    body: `We retain your email and usage data for as long as your account is active. You can request deletion of your data at any time by emailing info@creatovate.biz. We will process deletion requests within 30 days. Cancelling your subscription does not automatically delete your data — you must request this separately.`,
  },
  {
    heading: "Cookies & Local Storage",
    body: `We use localStorage in your browser to remember your login session. We do not use tracking cookies, advertising cookies, or any third-party analytics cookies. You can clear your browser storage at any time to log out.`,
  },
  {
    heading: "Your Rights",
    body: `You have the right to access, correct, or delete your personal data. If you are in the EU or UK, you have additional rights under GDPR including data portability and the right to object to processing. Contact info@creatovate.biz for any data requests or concerns.`,
  },
  {
    heading: "Security",
    body: `We use industry-standard security measures including encrypted connections (HTTPS), tokenised authentication (magic links with 15-minute expiry), and secure third-party payment processing. No method of transmission over the internet is 100% secure, but we take reasonable steps to protect your data.`,
  },
  {
    heading: "Changes to This Policy",
    body: `We may update this policy occasionally. We will notify you of material changes by email. Continued use of the service after changes constitutes acceptance of the updated policy.`,
  },
  {
    heading: "Contact",
    body: `For any privacy-related questions, email info@creatovate.biz. We aim to respond within 5 business days.`,
  },
];

const TERMS_SECTIONS = [
  {
    heading: "Acceptance of Terms",
    body: `By accessing or using PDFTopicScout, you agree to be bound by these Terms of Service. If you do not agree, do not use the service. These terms apply to all users, including subscribers and visitors.`,
  },
  {
    heading: "The Service",
    body: `PDFTopicScout provides AI-powered PDF topic research and scoring tools. Topic scores, insights, and outlines are AI-generated estimates for informational purposes only and should not be treated as guaranteed market data or financial advice. Results may vary.`,
  },
  {
    heading: "Subscription & Billing",
    body: `Access requires an active monthly subscription at the price displayed at checkout. Subscriptions renew automatically each month. You authorise us to charge your payment method on a recurring basis. Prices may change with 30 days notice to existing subscribers.`,
  },
  {
    heading: "Cancellation",
    body: `You can cancel your subscription at any time via the Manage Plan option in the app header. Cancellation takes effect at the end of the current billing period — you retain access until then. We do not prorate partial months.`,
  },
  {
    heading: "Refund Policy",
    body: `We offer a full refund within 7 days of your first subscription payment if you are not satisfied. Email info@creatovate.biz within 7 days of your first charge with your registered email address. No refunds are issued for subsequent billing periods or after the 7-day window.`,
  },
  {
    heading: "Acceptable Use",
    body: `You may not use PDFTopicScout to generate illegal, harmful, or defamatory content. You may not attempt to reverse-engineer, scrape, resell, or abuse the platform or its API. You may not share your account credentials. We reserve the right to suspend accounts that violate these terms without refund.`,
  },
  {
    heading: "Intellectual Property",
    body: `The PDFTopicScout platform, branding, and underlying code are owned by Creatovate. AI-generated topic suggestions, scores, and outlines produced during your session are yours to use freely for any lawful purpose. You may not resell or redistribute the platform itself.`,
  },
  {
    heading: "Disclaimer of Warranties",
    body: `The service is provided "as is" without warranties of any kind, express or implied. We do not guarantee that the service will be uninterrupted, error-free, or that AI-generated results will meet your specific requirements or expectations.`,
  },
  {
    heading: "Limitation of Liability",
    body: `Creatovate is not liable for any indirect, incidental, special, or consequential damages arising from your use of PDFTopicScout, including lost profits or data loss. Our total liability is limited to the amount you paid in the 30 days prior to the claim.`,
  },
  {
    heading: "Changes to the Service",
    body: `We may change, suspend, or discontinue any aspect of the service at any time. We will provide reasonable notice for material changes. Continued use after changes constitutes acceptance.`,
  },
  {
    heading: "Governing Law",
    body: `These terms are governed by applicable law. We will always attempt to resolve disputes in good faith before any formal proceedings. Contact us first at info@creatovate.biz.`,
  },
  {
    heading: "Contact",
    body: `Questions about these terms? Email info@creatovate.biz. We aim to respond within 5 business days.`,
  },
];

export default function Legal({ type, onBack }) {
  const isPrivacy = type === "privacy";
  const doc = isPrivacy ? PRIVACY_SECTIONS : TERMS_SECTIONS;
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f5", fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>

      <div style={{ borderBottom: "1px solid #e8e8e4", padding: "14px 28px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>PDFTopicScout</span>
          <span style={{ fontSize: 12, color: "#3b5bdb", fontWeight: 600 }}>by Creatovate</span>
        </button>
        <button onClick={onBack} style={{ background: "transparent", border: "1px solid #e8e8e4", color: "#666", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
          Back
        </button>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: "#1a1a1a", marginBottom: 6 }}>{title}</h1>
          <p style={{ fontSize: 13, color: "#aaa" }}>Last updated: May 13, 2026 &nbsp;·&nbsp; PDFTopicScout by Creatovate</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {doc.map((s, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", border: "1px solid #e8e8e4" }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 8 }}>
                {i + 1}. {s.heading}
              </h2>
              <p style={{ fontSize: 14, color: "#444", lineHeight: 1.8 }}>{s.body}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, textAlign: "center" }}>
          <span style={{ fontSize: 13, color: "#aaa" }}>
            Also see our{" "}
            <a href={isPrivacy ? "/terms" : "/privacy"} style={{ color: "#3b5bdb", fontWeight: 600, textDecoration: "none" }}>
              {isPrivacy ? "Terms of Service" : "Privacy Policy"}
            </a>
          </span>
        </div>
      </div>

      <div style={{ borderTop: "1px solid #e8e8e4", padding: "18px 28px", background: "#fff", textAlign: "center" }}>
        <span style={{ fontSize: 13, color: "#999" }}>
          <strong style={{ color: "#1a1a1a" }}>PDFTopicScout</strong>
          <span style={{ color: "#ccc", margin: "0 6px" }}>by</span>
          <strong style={{ color: "#3b5bdb" }}>Creatovate</strong>
          <span style={{ color: "#ccc", margin: "0 8px" }}>—</span>
          <a href="mailto:info@creatovate.biz" style={{ color: "#aaa", textDecoration: "none" }}>info@creatovate.biz</a>
        </span>
      </div>
    </div>
  );
}
